import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────
// useToast
//
// 일정 시간 후 자동으로 사라지는 토스트 메시지 상태 훅.
// 표시는 <Toast message={...} /> 컴포넌트가 담당한다.
// durationMs로 자동 소멸 시간을 조절한다(애니메이션 길이도 이 값과 맞추는 것을 권장).
// ─────────────────────────────────────────────

export interface UseToast {
  /** 현재 표시 중인 메시지 (없으면 null) */
  message: string | null;
  /** 토스트 표시 — durationMs 후 자동 소멸 */
  show: (msg: string) => void;
  /** 자동 소멸 시간(ms) — <Toast>의 애니메이션 길이와 맞추는 데 사용 */
  durationMs: number;
}

export const useToast = (durationMs = 4000): UseToast => {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(
    (msg: string) => {
      setMessage(msg);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMessage(null), durationMs);
    },
    [durationMs],
  );

  // 언마운트 시 타이머 정리
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { message, show, durationMs };
};
